import React from 'react';
import { useState, useEffect, useRef } from 'react';

import dayjs from 'lib/dayjs';
import { useGetSettingsQuery } from 'redux/features/settings/settingsApi';
import { useGetDetailsQuery } from 'redux/features/users/authApi';
import { useGetFamilyDetailsByUserIdQuery } from 'redux/features/familyDetailsApi';
import { useGetContactNumbersQuery } from 'redux/features/users/userApi';
import Icon from '../../../components/AppIcon';
import { getLeaveTypeEnum, LEAVE_TYPES, mapLeaveType } from '../../../utils/common';
import Button from '../../../components/ui/Button';

const AddLeaveModal = ({
  isOpen,
  onClose,
  isEditMode = false,
  leaveToEdit = null,
  selectedDate,
  onAddLeave,
  onUpdateLeave,
  isAdminMode = false,
  selectedEmployeeId = null,
  skipModalWrapper = false,
  employees = [],
  isSubmitting = false,
  defaultLeaveType = '',
  lockedDates = null,
}) => {
  // Unplanned leave: dates (and therefore the leave type) are fixed, only the reason is entered
  const isLocked = !!lockedDates?.length;
  const actionButtonsRef = useRef(null);
  const modalContentRef = useRef(null);
  const [leaveType, setLeaveType] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [contactOption, setContactOption] = useState('');
  const [customContactName, setCustomContactName] = useState('');
  const [customContactNumber, setCustomContactNumber] = useState('');
  const [contactNameError, setContactNameError] = useState('');
  const [contactNumberError, setContactNumberError] = useState('');
  const [employeeError, setEmployeeError] = useState('');
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState({
    startDateTime: '',
    endDateTime: '',
    dates: [],
    halfDayType: 'FIRST_HALF',
    reason: '',
    user_id: null,
  });
  const { data: settingsData } = useGetSettingsQuery();
  const { data: userDetails } = useGetDetailsQuery();
  const currentUserId = userDetails?.id;
  // Employees pick from their own contacts; in admin mode the contacts belong to the
  // selected employee, not the logged-in admin (whose id lives in a separate table)
  const { data: familyMembers } = useGetFamilyDetailsByUserIdQuery(currentUserId, {
    skip: !currentUserId || isAdminMode,
  });
  const { data: employeeContacts } = useGetContactNumbersQuery(selectedEmployee, {
    skip: !isAdminMode || !selectedEmployee,
  });
  const [workSchedule, setWorkScheduleState] = useState({
    dayStartTime: '',
    dayEndTime: '',
    breakStartTime: '',
    breakEndTime: '',
    fullDayWorkHours: 0,
    halfDayWorkHours: 0,
  });
  useEffect(() => {
    if (settingsData?.data) {
      setWorkScheduleState({
        dayStartTime: settingsData?.data?.day_start_time,
        dayEndTime: settingsData?.data?.day_end_time,
        breakStartTime: settingsData?.data?.break_start_time,
        breakEndTime: settingsData?.data?.break_end_time,
        fullDayWorkHours: settingsData?.data?.full_day_working_hours || 0,
        halfDayWorkHours: settingsData?.data?.half_day_working_hours || 0,
      });
    }
  }, [settingsData]);
  // Calendar state for Multiple Day Leave
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  // Auto-scroll to top when modal opens
  useEffect(() => {
    if (isOpen && modalContentRef.current) {
      modalContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (isOpen && selectedDate) {
      const dateKey = `${selectedDate?.getFullYear()}-${String(selectedDate?.getMonth() + 1)?.padStart(2, '0')}-${String(selectedDate?.getDate())?.padStart(2, '0')}`;
      setFormData((prev) => ({ ...prev, startDateTime: dateKey }));
    }
  }, [isOpen, selectedDate]);

  React.useEffect(() => {
    if (!isOpen || !lockedDates?.length) return;
    const sorted = [...lockedDates].sort();
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    if (sorted.length === 1) {
      setLeaveType(LEAVE_TYPES.FULL_DAY);
      setFormData((prev) => ({ ...prev, startDateTime: first, endDateTime: first, dates: [] }));
    } else if (sorted.length < 3) {
      setLeaveType(LEAVE_TYPES.MULTIPLE_DAY);
      setFormData((prev) => ({ ...prev, startDateTime: first, endDateTime: last, dates: sorted }));
    } else {
      setLeaveType(LEAVE_TYPES.LONG_TIME);
      setFormData((prev) => ({ ...prev, startDateTime: first, endDateTime: last, dates: [] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, lockedDates?.join(',')]);

  React.useEffect(() => {
    if (isOpen) {
      setSelectedEmployee(selectedEmployeeId || '');
      if (!isEditMode && defaultLeaveType) {
        setLeaveType(defaultLeaveType);
      }
    }
  }, [isOpen, selectedEmployeeId, isEditMode, defaultLeaveType]);

  useEffect(() => {
    if (actionButtonsRef.current) {
      actionButtonsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [leaveType]);

  // Pre-populate form when editing
  React.useEffect(() => {
    if (isOpen && isEditMode && leaveToEdit) {
      setLeaveType(leaveToEdit.type);

      // Extract date/time info based on leave type
      if (leaveToEdit.start_date_time) {
        // For short leave, we need the full datetime string (with time)
        // For other types, we just need the date
        let startDateTime, endDateTime;

        if (leaveToEdit.type === LEAVE_TYPES.SHORT) {
          // Keep the full datetime string for short leave (e.g., "2024-11-25T14:30:00")
          startDateTime = leaveToEdit.start_date_time; // Remove milliseconds if any
          endDateTime = leaveToEdit.end_date_time;
        } else {
          // For other types, just get the date portion
          startDateTime = leaveToEdit.start_date_time.split('T')[0];
          endDateTime = leaveToEdit.end_date_time?.split('T')[0] || '';
        }

        setFormData({
          startDateTime,
          endDateTime,
          dates: leaveToEdit.dates || [],
          halfDayType: leaveToEdit.half_day_type || 'FIRST_HALF',
          reason: leaveToEdit.reason || '',
        });

        if (leaveToEdit.contact_number) {
          try {
            const parsed = JSON.parse(leaveToEdit.contact_number);
            const name = parsed.name || '';
            const number = parsed.number || '';
            if (name === 'User') setContactOption('user');
            else if (name === 'Father') setContactOption('father');
            else if (name === 'Mother') setContactOption('mother');
            else if (name === 'Emergency Contact') setContactOption('emergency');
            else {
              setContactOption('other');
              setCustomContactName(name);
              setCustomContactNumber(number);
            }
          } catch {
            // Legacy format — plain number string
            setContactOption('other');
            setCustomContactName('');
            setCustomContactNumber(leaveToEdit.contact_number);
          }
        }
      }
    }
  }, [isOpen, isEditMode, leaveToEdit]);

  const handleLeaveTypeChange = (type) => {
    setLeaveType(type);
    setFormError('');
    setFormData({
      startDateTime: formData?.startDateTime,
      endDateTime: '',
      dates: [],
      halfDayType: 'FIRST_HALF',
      reason: '',
    });
  };

  const fatherMember = (familyMembers || []).find((m) => m.relation === 'Father');
  const motherMember = (familyMembers || []).find((m) => m.relation === 'Mother');
  const contacts = isAdminMode
    ? {
        mobile: employeeContacts?.mobile,
        father: employeeContacts?.father_contact_number,
        mother: employeeContacts?.mother_contact_number,
        emergency: employeeContacts?.emergency_contact_number,
      }
    : {
        mobile: userDetails?.mobile,
        father: fatherMember?.contact_number,
        mother: motherMember?.contact_number,
        emergency: userDetails?.emergency_contact_number,
      };

  const contactOptions = [
    ...(contacts.mobile
      ? [
          {
            value: 'user',
            label: `User - ${contacts.mobile}`,
            name: 'User',
            number: contacts.mobile,
          },
        ]
      : []),
    ...(contacts.father
      ? [
          {
            value: 'father',
            label: `Father - ${contacts.father}`,
            name: 'Father',
            number: contacts.father,
          },
        ]
      : []),
    ...(contacts.mother
      ? [
          {
            value: 'mother',
            label: `Mother - ${contacts.mother}`,
            name: 'Mother',
            number: contacts.mother,
          },
        ]
      : []),
    ...(contacts.emergency
      ? [
          {
            value: 'emergency',
            label: `Emergency Contact - ${contacts.emergency}`,
            name: 'Emergency Contact',
            number: contacts.emergency,
          },
        ]
      : []),
    { value: 'other', label: 'Other', name: '', number: '' },
  ];

  const getSelectedContactNumber = () => {
    const selected = contactOptions.find((opt) => opt.value === contactOption);
    if (contactOption === 'other') {
      if (!customContactName?.trim() && !customContactNumber?.trim()) return '';
      return JSON.stringify({
        name: customContactName.trim() || 'Other',
        number: customContactNumber.trim(),
      });
    }
    if (selected && selected.number) {
      return JSON.stringify({ name: selected.name, number: selected.number });
    }
    return '';
  };

  const getContactNumberOnly = (contactStr) => {
    if (!contactStr) return '';
    try {
      const parsed = JSON.parse(contactStr);
      return parsed.number || '';
    } catch {
      return contactStr;
    }
  };

  const isLeaveChanged = () => {
    if (!isEditMode || !leaveToEdit) return true;

    if (leaveType !== leaveToEdit.type) return true;
    if (formData.reason !== (leaveToEdit.reason || '')) return true;
    if (formData.halfDayType !== (leaveToEdit.half_day_type || 'FIRST_HALF')) return true;

    const currentContact = getContactNumberOnly(getSelectedContactNumber());
    const savedContact = getContactNumberOnly(leaveToEdit.contact_number);
    if (currentContact !== savedContact) return true;

    // Check dates
    if (leaveToEdit.type === LEAVE_TYPES.MULTIPLE_DAY_LEAVE) {
      if (formData.dates.length !== (leaveToEdit.dates || []).length) return true;
      if (!formData.dates.every((d) => (leaveToEdit.dates || []).includes(d))) return true;
    } else if (leaveToEdit.type === LEAVE_TYPES.SHORT) {
      if (formData.startDateTime !== leaveToEdit.start_date_time) return true;
      if (formData.endDateTime !== leaveToEdit.end_date_time) return true;
    } else {
      // FULL_DAY, HALF_DAY, LONG_TIME
      const currentStart = formData.startDateTime;
      const initialStart = leaveToEdit.start_date_time?.split('T')[0] || '';
      if (currentStart !== initialStart) return true;

      if (leaveType === LEAVE_TYPES.LONG_TIME_LEAVE) {
        const currentEnd = formData.endDateTime;
        const initialEnd = leaveToEdit.end_date_time?.split('T')[0] || '';
        if (currentEnd !== initialEnd) return true;
      }
    }

    return false;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormError('');
  };

  // Helper functions for short leave form
  const getShortLeaveDate = () => {
    if (formData?.startDateTime && formData?.startDateTime.includes('T')) {
      return formData?.startDateTime.split('T')[0];
    }
    return formData?.startDateTime || '';
  };

  const getShortLeaveStartTime = () => {
    if (formData?.startDateTime && formData?.startDateTime.includes('T')) {
      const time = `${dayjs(formData?.startDateTime).format('HH:mm')}`;
      return time;
    }
    return '';
  };

  const getShortLeaveEndTime = () => {
    if (formData?.endDateTime && formData?.endDateTime.includes('T')) {
      const time = `${dayjs(formData?.endDateTime).format('HH:mm')}`;
      return time;
    }
    return '';
  };

  const handleShortLeaveDateChange = (date) => {
    const startTime = getShortLeaveStartTime();
    const endTime = getShortLeaveEndTime();

    if (startTime) {
      setFormData((prev) => ({ ...prev, startDateTime: `${date}T${startTime}:00` }));
    } else {
      setFormData((prev) => ({ ...prev, startDateTime: date }));
    }

    if (endTime) {
      setFormData((prev) => ({ ...prev, endDateTime: `${date}T${endTime}:00` }));
    }
  };

  const handleShortLeaveStartTimeChange = (time) => {
    const date = getShortLeaveDate();
    if (date) {
      setFormData((prev) => ({ ...prev, startDateTime: `${date}T${time}:00` }));
    }
  };

  const handleShortLeaveEndTimeChange = (time) => {
    const date = getShortLeaveDate();
    if (date) {
      setFormData((prev) => ({ ...prev, endDateTime: `${date}T${time}:00` }));
    }
  };

  // Calendar helper functions for Multiple Day Leave
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add days of the month
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const cellDate = new Date(currentYear, currentMonth, day);
      cellDate.setHours(0, 0, 0, 0);

      days.push({
        day,
        dateStr,
        isSelected: formData?.dates?.includes(dateStr),
        isPast: !isAdminMode && cellDate < today,
      });
    }

    return days;
  };

  const handleMonthChange = (month) => {
    setCurrentMonth(month);
  };

  const handleYearChange = (year) => {
    setCurrentYear(year);
  };

  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  const handleMultipleDatesChange = (dateStr) => {
    // Prevent selecting past dates in employee mode
    if (!isAdminMode) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const targetDate = new Date(dateStr);
      if (targetDate < today) return;
    }

    const dates = formData?.dates || [];
    if (dates?.includes(dateStr)) {
      setFormData((prev) => ({ ...prev, dates: dates?.filter((d) => d !== dateStr) }));
    } else {
      setFormData((prev) => ({ ...prev, dates: [...dates, dateStr] }));
    }
  };

  const handleClose = () => {
    setLeaveType('');
    setSelectedEmployee('');
    setContactOption('');
    setCustomContactName('');
    setCustomContactNumber('');
    setEmployeeError('');
    setContactNameError('');
    setContactNumberError('');
    setFormError('');
    setFormData({
      startDateTime: '',
      endDateTime: '',
      dates: [],
      halfDayType: 'FIRST_HALF',
      reason: '',
    });
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    // Validate employee selection (required for both admin and employee modes)
    if (isAdminMode && !selectedEmployee) {
      setEmployeeError('Please select an employee');
      if (modalContentRef.current) {
        modalContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }

    // Validate based on leave type
    if (
      leaveType === LEAVE_TYPES.LONG_TIME &&
      (!formData?.startDateTime || !formData?.endDateTime)
    ) {
      alert('Please select start and end dates');
      return;
    }
    if (leaveType === LEAVE_TYPES.MULTIPLE_DAY && formData?.dates?.length === 0) {
      alert('Please select at least one date');
      return;
    }
    if (leaveType === LEAVE_TYPES.SHORT && (!formData?.startDateTime || !formData?.endDateTime)) {
      alert('Please select date, start time, and end time');
      return;
    }
    if (!formData?.reason?.trim()) {
      alert('Please provide a reason');
      return;
    }

    if (
      (leaveType === LEAVE_TYPES.LONG_TIME || leaveType === LEAVE_TYPES.MULTIPLE_DAY) &&
      !getSelectedContactNumber()?.trim()
    ) {
      setFormError('Please select a contact number');
      return;
    }

    if (contactOption === 'other') {
      let valid = true;
      if (!customContactName?.trim()) {
        setContactNameError('Please enter a contact name');
        valid = false;
      } else if (customContactName.trim().length < 3) {
        setContactNameError('Contact name must be at least 3 characters long');
        valid = false;
      } else {
        setContactNameError('');
      }
      if (!customContactNumber?.trim()) {
        setContactNumberError('Please enter a contact number');
        valid = false;
      } else if (!/^\d{10}$/.test(customContactNumber.trim())) {
        setContactNumberError('Contact number must be a 10-digit number');
        valid = false;
      } else {
        setContactNumberError('');
      }
      if (!valid) return;
    } else {
      setContactNameError('');
      setContactNumberError('');
    }

    formData.user_id = selectedEmployee;
    // Create consistent leave data with proper time calculations
    const leaveData = createConsistentLeaveData(leaveType, formData);
    // If editing, include the leave ID
    if (isEditMode && leaveToEdit?.id) {
      await onUpdateLeave?.({ id: leaveToEdit.id, ...leaveData });
    } else {
      await onAddLeave?.(leaveData);
    }
    handleClose();
  };

  const createConsistentLeaveData = (leaveType, formData) => {
    // Get work schedule times with fallbacks

    const baseData = {
      type: getLeaveTypeEnum(leaveType),
      reason: formData?.reason,
      contact_number:
        leaveType === LEAVE_TYPES.LONG_TIME || leaveType === LEAVE_TYPES.MULTIPLE_DAY
          ? getSelectedContactNumber()
          : undefined,
      status: isAdminMode ? 'APPROVED' : 'PENDING',
      user_id: formData?.user_id,
    };

    switch (leaveType) {
      case LEAVE_TYPES.FULL_DAY: {
        const startDate = dayjs(
          `${formData?.startDateTime}T${workSchedule.dayStartTime}:00`,
        ).toISOString();
        const endDate = dayjs(
          `${formData?.startDateTime}T${workSchedule.dayEndTime}:00`,
        ).toISOString();
        return {
          ...baseData,
          start_date_time: startDate,
          end_date_time: endDate,
          duration: '1 day',
        };
      }

      case LEAVE_TYPES.HALF_DAY: {
        const halfDayStart =
          formData?.halfDayType === 'FIRST_HALF'
            ? workSchedule.dayStartTime
            : workSchedule.breakEndTime;
        const halfDayStartDate = dayjs(`${formData?.startDateTime}T${halfDayStart}:00`);
        const halfDayEndTime =
          formData?.halfDayType === 'FIRST_HALF'
            ? workSchedule.breakStartTime
            : workSchedule.dayEndTime;
        const halfDayEndDate = dayjs(`${formData?.startDateTime}T${halfDayEndTime}:00`);
        return {
          ...baseData,
          start_date_time: halfDayStartDate.toISOString(),
          end_date_time: halfDayEndDate.toISOString(),
          half_day_type: formData?.halfDayType,
          duration: calculateDuration(halfDayStartDate, halfDayEndDate),
        };
      }

      case LEAVE_TYPES.SHORT:
        return {
          ...baseData,
          start_date_time: dayjs(formData?.startDateTime).toISOString(),
          end_date_time: dayjs(formData?.endDateTime).toISOString(),
          duration: calculateDuration(formData?.startDateTime, formData?.endDateTime),
        };

      case LEAVE_TYPES.LONG_TIME: {
        const daysDiff = calculateDaysDifference(formData?.startDateTime, formData?.endDateTime);
        const longTimeDates = [];
        const end = dayjs(formData?.endDateTime);

        // Create array of dates starting from end date (descending)
        for (let i = 0; i < daysDiff; i++) {
          longTimeDates.push(end.subtract(i, 'day').format('YYYY-MM-DD'));
        }

        return {
          ...baseData,
          start_date_time: dayjs(`${formData?.startDateTime}T00:00:00`).toISOString(),
          end_date_time: dayjs(`${formData?.endDateTime}T23:59:59`).toISOString(),
          dates: longTimeDates,
          duration: `${daysDiff} day${daysDiff > 1 ? 's' : ''}`,
        };
      }

      case LEAVE_TYPES.MULTIPLE_DAY: {
        const sortedDates = [...(formData?.dates || [])].sort().reverse();
        const firstDate = sortedDates[sortedDates.length - 1]; // First date is now at the end
        const lastDate = sortedDates[0]; // Last date is now at the beginning

        return {
          ...baseData,
          start_date_time: firstDate
            ? dayjs(`${firstDate}T${workSchedule.dayStartTime}:00`).toISOString()
            : dayjs().toISOString(),
          end_date_time: lastDate
            ? dayjs(`${lastDate}T${workSchedule.dayEndTime}:00`).toISOString()
            : dayjs().toISOString(),
          dates: sortedDates,
          duration: `${sortedDates.length} day${sortedDates.length > 1 ? 's' : ''}`,
        };
      }

      default:
        return baseData;
    }
  };

  const calculateDuration = (startDateTime, endDateTime) => {
    if (!startDateTime || !endDateTime) return '';

    const start = new Date(startDateTime);
    const end = new Date(endDateTime);
    const diffMs = end - start;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffMinutes >= 60) {
      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    } else {
      return `${diffMinutes} minutes`;
    }
  };

  const calculateDaysDifference = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  if (!isOpen) return null;

  const renderModalContent = () => (
    <div
      ref={modalContentRef}
      className="bg-surface rounded-lg border border-border w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <h2 className="text-xl font-bold text-text-primary">
          {isAdminMode
            ? 'Add Leave on Behalf of Employee'
            : isEditMode
              ? 'Edit Leave'
              : 'Apply for Leave'}
        </h2>
        <button
          onClick={handleClose}
          className="p-2 text-text-secondary hover:text-text-primary hover:bg-secondary-100 rounded-lg transition-colors duration-200"
        >
          <Icon name="X" size={20} />
        </button>
      </div>

      {/* Content */}
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Employee Selection (Admin Mode Only) */}
        {isAdminMode && (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-text-primary">Select Employee</label>
            <SearchableSelect
              options={
                employees?.map((employee) => ({
                  value: employee?.id,
                  label: `${employee?.employee_id} - ${employee?.first_name} ${employee?.last_name}`,
                })) || []
              }
              value={selectedEmployee}
              onChange={(val) => {
                setSelectedEmployee(val);
                setEmployeeError('');
                setContactOption('');
              }}
              placeholder="Select Employee"
              searchPlaceholder="Search employees by name or ID..."
              disabled={!!selectedEmployeeId}
              required
            />
            {employeeError && <p className="text-xs text-red-500 mt-1">{employeeError}</p>}
          </div>
        )}

        {/* Leave Type Selection */}
        {!isLocked && (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-text-primary">Leave Type</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { type: LEAVE_TYPES.FULL_DAY, icon: 'Calendar', description: 'Full working day' },
                { type: LEAVE_TYPES.HALF_DAY, icon: 'Clock', description: 'Half working day' },
                {
                  type: LEAVE_TYPES.LONG_TIME,
                  icon: 'CalendarRange',
                  description: 'Continuous days',
                },
                {
                  type: LEAVE_TYPES.MULTIPLE_DAY,
                  icon: 'CalendarDays',
                  description: 'Non-contiguous dates',
                },
                { type: LEAVE_TYPES.SHORT, icon: 'Timer', description: 'Few hours' },
              ]?.map(({ type, icon, description }) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => handleLeaveTypeChange(type)}
                  className={`p-4 rounded-lg border text-left transition-colors duration-200 ${
                    leaveType === type
                      ? 'bg-primary-50 border-primary text-primary'
                      : 'bg-background border-border text-text-primary hover:bg-secondary-50'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <Icon name={icon} size={20} />
                    <div>
                      <p className="font-medium">{mapLeaveType(type)}</p>
                      <p className="text-sm opacity-70">{description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Date/Duration Fields */}
        {leaveType && (
          <div className="space-y-4">
            {isLocked && (
              <div className="rounded-lg border border-border bg-secondary-50 p-4">
                <p className="text-sm font-medium text-text-primary">
                  Unplanned leave · {mapLeaveType(leaveType)}
                </p>
                <p className="mb-2 text-xs text-text-secondary">
                  These dates are fixed. Add the reason for your absence and submit for approval.
                </p>
                <div className="flex flex-wrap gap-2">
                  {[...lockedDates].sort().map((d) => (
                    <span
                      key={d}
                      className="rounded-md border border-border bg-white px-2.5 py-1 text-xs font-semibold text-text-primary"
                    >
                      {dayjs(d).format('ddd, DD MMM YYYY')}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {leaveType === LEAVE_TYPES.FULL_DAY && !isLocked && (
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Date</label>
                <input
                  type="date"
                  value={formData?.startDateTime}
                  min={!isAdminMode ? new Date().toISOString().split('T')[0] : undefined}
                  onChange={(e) => handleInputChange('startDateTime', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  required
                />
              </div>
            )}

            {leaveType === LEAVE_TYPES.HALF_DAY && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">Date</label>
                  <input
                    type="date"
                    value={formData?.startDateTime}
                    min={!isAdminMode ? new Date().toISOString().split('T')[0] : undefined}
                    onChange={(e) => handleInputChange('startDateTime', e?.target?.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Half Day Type
                  </label>
                  <select
                    value={formData?.halfDayType}
                    onChange={(e) => handleInputChange('halfDayType', e?.target?.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  >
                    <option value="FIRST_HALF">First Half (Morning)</option>
                    <option value="SECOND_HALF">Second Half (Afternoon)</option>
                  </select>
                </div>
              </div>
            )}

            {leaveType === LEAVE_TYPES.SHORT && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">Date</label>
                  <input
                    type="date"
                    value={getShortLeaveDate()}
                    min={!isAdminMode ? new Date().toISOString().split('T')[0] : undefined}
                    onChange={(e) => handleShortLeaveDateChange(e?.target?.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Start Time
                    </label>
                    <input
                      type="time"
                      value={getShortLeaveStartTime()}
                      onChange={(e) => {
                        const endTime = getShortLeaveEndTime();
                        if (endTime && e?.target?.value > endTime) return;
                        handleShortLeaveStartTimeChange(e?.target?.value);
                      }}
                      disabled={!getShortLeaveDate()}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      End Time
                    </label>
                    <input
                      type="time"
                      value={getShortLeaveEndTime()}
                      onChange={(e) => {
                        const startTime = getShortLeaveStartTime();
                        if (!startTime || e.target.value > startTime) {
                          handleShortLeaveEndTimeChange(e.target.value);
                        }
                      }}
                      disabled={!getShortLeaveStartTime()}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            {leaveType === LEAVE_TYPES.LONG_TIME && !isLocked && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={formData?.startDateTime}
                      min={!isAdminMode ? new Date().toISOString().split('T')[0] : undefined}
                      onChange={(e) => {
                        if (formData?.endDateTime && e?.target?.value > formData?.endDateTime)
                          return;
                        handleInputChange('startDateTime', e?.target?.value);
                      }}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={formData?.endDateTime}
                      min={
                        !isAdminMode
                          ? formData?.startDateTime || new Date().toISOString().split('T')[0]
                          : undefined
                      }
                      onChange={(e) => {
                        if (formData?.startDateTime && e?.target?.value < formData?.startDateTime)
                          return;
                        handleInputChange('endDateTime', e?.target?.value);
                      }}
                      disabled={!formData?.startDateTime}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            {leaveType === LEAVE_TYPES.MULTIPLE_DAY && !isLocked && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Select Dates
                  </label>

                  {/* Month and Year Selectors */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <select
                          value={currentMonth}
                          onChange={(e) => handleMonthChange(parseInt(e.target.value))}
                          className="appearance-none px-4 py-2 pr-8 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                          style={{ backgroundImage: 'none' }}
                          required
                        >
                          {[
                            'January',
                            'February',
                            'March',
                            'April',
                            'May',
                            'June',
                            'July',
                            'August',
                            'September',
                            'October',
                            'November',
                            'December',
                          ].map((month, index) => (
                            <option key={index} value={index}>
                              {month}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                          <Icon name="ChevronDown" size={16} color="var(--color-text-secondary)" />
                        </div>
                      </div>

                      <div className="relative">
                        <select
                          value={currentYear}
                          onChange={(e) => handleYearChange(parseInt(e.target.value))}
                          className="appearance-none px-4 py-2 pr-8 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                          style={{ backgroundImage: 'none' }}
                          required
                        >
                          {Array.from({ length: 10 }, (_, i) => {
                            const year = new Date().getFullYear() - 2 + i;
                            return (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            );
                          })}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                          <Icon name="ChevronDown" size={16} color="var(--color-text-secondary)" />
                        </div>
                      </div>
                    </div>

                    {/* Navigation Buttons */}
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => navigateMonth('prev')}
                        className="p-2 text-text-secondary hover:text-text-primary hover:bg-secondary-50 rounded-lg transition-colors duration-200"
                      >
                        <Icon name="ChevronLeft" size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => navigateMonth('next')}
                        className="p-2 text-text-secondary hover:text-text-primary hover:bg-secondary-50 rounded-lg transition-colors duration-200"
                      >
                        <Icon name="ChevronRight" size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Calendar Grid */}
                  <div className="border border-border rounded-lg p-4">
                    {/* Day Headers */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                        <div
                          key={day}
                          className="p-2 text-center text-xs font-medium text-text-secondary"
                        >
                          {day}
                        </div>
                      ))}
                    </div>

                    {/* Calendar Days */}
                    <div className="grid grid-cols-7 gap-1">
                      {generateCalendarDays().map((dayData, index) => {
                        if (!dayData) {
                          return <div key={index} className="p-2"></div>;
                        }

                        return (
                          <button
                            key={dayData.dateStr}
                            type="button"
                            disabled={dayData.isPast}
                            onClick={() => handleMultipleDatesChange(dayData.dateStr)}
                            className={`p-2 text-xs rounded-lg border transition-colors duration-200 ${
                              dayData.isSelected
                                ? 'bg-primary text-white border-primary'
                                : dayData.isPast
                                  ? 'bg-secondary-50 border-border text-text-tertiary cursor-not-allowed opacity-50'
                                  : 'bg-background border-border text-text-primary hover:bg-secondary-50'
                            }`}
                          >
                            {dayData.day}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <p className="text-sm text-text-secondary mt-2">
                    Selected: {formData?.dates?.length || 0} day(s)
                  </p>
                </div>
              </div>
            )}

            {/* Reason */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Reason</label>
              <textarea
                value={formData?.reason}
                onChange={(e) => handleInputChange('reason', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                rows={3}
                placeholder="Enter reason for leave"
                required
              />
            </div>

            {(leaveType === LEAVE_TYPES.LONG_TIME || leaveType === LEAVE_TYPES.MULTIPLE_DAY) && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-text-primary">
                  Contact Number During Leave
                </label>
                <select
                  value={contactOption}
                  onChange={(e) => {
                    setContactOption(e.target.value);
                    setFormError('');
                  }}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                >
                  <option value="">Select contact number</option>
                  {contactOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {contactOption === 'other' && (
                  <div className="space-y-2">
                    <div>
                      <input
                        type="text"
                        value={customContactName}
                        onChange={(e) => {
                          setCustomContactName(e.target.value);
                          setContactNameError('');
                        }}
                        placeholder="Enter contact name"
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 ${contactNameError ? 'border-red-500' : 'border-border'}`}
                      />
                      {contactNameError && (
                        <p className="text-xs text-red-500 mt-1">{contactNameError}</p>
                      )}
                    </div>
                    <div>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={customContactNumber}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                          setCustomContactNumber(val);
                          setContactNumberError('');
                        }}
                        placeholder="Enter 10-digit contact number"
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 ${contactNumberError ? 'border-red-500' : 'border-border'}`}
                      />
                      {contactNumberError && (
                        <p className="text-xs text-red-500 mt-1">{contactNumberError}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Form Error */}
        {formError && (
          <div className="p-3 rounded-lg bg-red-50 border border-red-200">
            <p className="text-sm text-red-600">{formError}</p>
          </div>
        )}

        {/* Action Buttons */}
        {leaveType && (
          <div ref={actionButtonsRef} className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2 text-text-secondary hover:text-text-primary transition-colors duration-200"
            >
              Cancel
            </button>
            <Button
              type="submit"
              disabled={isSubmitting || (isEditMode && !isLeaveChanged())}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[140px]"
              loading={isSubmitting}
            >
              {isEditMode ? 'Update Leave' : isAdminMode ? 'Add Leave' : 'Submit Leave Request'}
            </Button>
          </div>
        )}
      </form>
    </div>
  );

  if (skipModalWrapper) {
    return renderModalContent();
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      {renderModalContent()}
    </div>
  );
};

// SearchableSelect component moved inline
const SearchableSelect = ({
  options = [],
  value,
  onChange,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search...',
  className = '',
  disabled = false,
  required = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter options based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredOptions(options);
    } else {
      const filtered = options?.filter((option) =>
        option?.label?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
      }
    }
  };

  const handleSelect = (option) => {
    onChange(option.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  const selectedOption = options.find((option) => option.value === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`
          w-full p-2 border border-border rounded-lg 
          focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
          transition-colors duration-200 text-left
          ${disabled ? 'bg-gray-100 cursor-not-allowed text-gray-400' : 'bg-background hover:border-primary-300'}
          ${required && !value ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}
        `}
      >
        <div className="flex items-center justify-between">
          <span className={selectedOption ? 'text-text-primary' : 'text-text-secondary'}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <Icon
            name={isOpen ? 'ChevronUp' : 'ChevronDown'}
            size={16}
            className="text-text-secondary flex-shrink-0 ml-2"
          />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-border rounded-lg shadow-lg max-h-60 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Icon
                name="Search"
                size={16}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary"
              />
              <input
                ref={searchInputRef}
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option)}
                  className={`
                    w-full px-3 py-2 text-left text-sm hover:bg-secondary-50 transition-colors duration-150
                    ${value === option.value ? 'bg-primary-50 text-primary-600 font-medium' : 'text-text-primary'}
                  `}
                >
                  {option.label}
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-text-secondary text-center">
                No options found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddLeaveModal;
